import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Form, notification, Spin } from 'antd'
import dayjs from 'dayjs'
import { Drawer, DrawerFooter } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { EventForm } from '@/components/form/EventForm'
import type { EventFormValues } from '@/components/form/EventForm'
import { createEvent, updateEvent, getEventById } from '@/api/events.api'
import type { EventItem } from '@/types/api'

interface EventFormDrawerProps {
  isOpen: boolean
  mode: 'create' | 'update'
  event: EventItem | null
  onClose: () => void
}

export function EventFormDrawer({
  isOpen,
  mode,
  event: listItem,
  onClose,
}: EventFormDrawerProps) {
  const [form] = Form.useForm<EventFormValues>()
  const queryClient = useQueryClient()

  const { data: eventDetail, isLoading: isFetching } = useQuery({
    queryKey: ['event-detail', listItem?.id],
    queryFn: ({ signal }) =>
      listItem?.id ? getEventById(listItem.id, signal) : Promise.resolve(null),
    enabled: isOpen && mode === 'update' && !!listItem?.id,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  useEffect(() => {
    if (isOpen) {
      if (mode === 'update' && eventDetail) {
        const mediaList =
          eventDetail.eventMedias || eventDetail.mediaFiles || []
        form.setFieldsValue({
          id: eventDetail.id,
          name: eventDetail.name,
          eventType: eventDetail.eventType || 1,
          mediaType: eventDetail.mediaType || 1,
          mediaShowtime:
            eventDetail.mediaType === 1
              ? 'Mặc định theo thời gian phát của video'
              : eventDetail.mediaShowtime,
          startDate: eventDetail.startDate
            ? dayjs(eventDetail.startDate)
            : null,
          endDate: eventDetail.endDate ? dayjs(eventDetail.endDate) : null,
          items: mediaList.length
            ? mediaList.map((item: any) => {
                const videoObj = item.videoFileResponse || item.video
                const imageObj = item.imageFileResponse || item.image
                const audioObj = item.audioFileResponse || item.audio

                return {
                  id: item.id,
                  video: videoObj
                    ? [
                        {
                          uid: videoObj.id || item.videoId,
                          name: videoObj.originFileName || 'video.mp4',
                          originFileName: videoObj.originFileName,
                          status: 'done',
                          id: videoObj.id || item.videoId,
                          url: videoObj.fileUrl,
                        },
                      ]
                    : [],
                  image: imageObj
                    ? [
                        {
                          uid: imageObj.id || item.imageId,
                          name: imageObj.originFileName || 'image.png',
                          originFileName: imageObj.originFileName,
                          status: 'done',
                          id: imageObj.id || item.imageId,
                          url: imageObj.fileUrl,
                        },
                      ]
                    : [],
                  audio: audioObj
                    ? [
                        {
                          uid: audioObj.id || item.audioId,
                          name: audioObj.originFileName || 'audio.mp3',
                          originFileName: audioObj.originFileName,
                          status: 'done',
                          id: audioObj.id || item.audioId,
                          url: audioObj.fileUrl,
                        },
                      ]
                    : [],
                }
              })
            : [{}],
        })
      } else if (mode === 'create') {
        form.resetFields()
      }
    } else {
      form.resetFields()
    }
  }, [isOpen, mode, eventDetail, form])

  const createMutation = useMutation({
    mutationFn: createEvent,
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Thêm mới sự kiện thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['events'] })
      onClose()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (formData: FormData) => {
      if (!listItem?.id) throw new Error('Missing event ID')
      return updateEvent(listItem.id, formData)
    },
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Cập nhật sự kiện thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['events'] })
      onClose()
    },
  })

  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const isDrawerDisabled = isSubmitting || isFetching

  const handleSubmit = (values: EventFormValues) => {
    const formData = new FormData()

    formData.append('name', values.name.trim())
    formData.append(
      'startDate',
      values.startDate
        ? dayjs(values.startDate).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
        : '',
    )
    formData.append(
      'endDate',
      values.endDate
        ? dayjs(values.endDate).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
        : '',
    )
    formData.append('description', values.description || '')
    formData.append('mediaType', String(values.mediaType))
    formData.append(
      'status',
      String(mode === 'update' ? (eventDetail?.status ?? 1) : 1),
    )
    formData.append('eventType', String(values.eventType || 1))

    if (values.mediaType === 2 && values.mediaShowtime) {
      formData.append('mediaShowtime', String(values.mediaShowtime))
    }

    // Helper bóc tách ID từ file response
    const extractFileId = (fileField: any, fallbackId?: string | null) => {
      if (!fileField) return null
      const fileList = Array.isArray(fileField)
        ? fileField
        : fileField?.fileList || []
      const file = fileList.length > 0 ? fileList[fileList.length - 1] : null
      if (!file) return null

      return (
        file.id ||
        file.response?.id ||
        file.response?.data?.responseData?.id ||
        fallbackId ||
        null
      )
    }

    // Build payload mediaFiles dựa theo mediaType
    const mediaFiles = (values.items || []).map((item, index) => {
      const existingMedia =
        eventDetail?.eventMedias?.[index] || eventDetail?.mediaFiles?.[index]

      let mediaItem: Record<string, any> = {}

      if (values.mediaType === 1) {
        mediaItem = {
          videoId: extractFileId(item.video, existingMedia?.videoId),
        }
      } else {
        mediaItem = {
          imageId: extractFileId(item.image, existingMedia?.imageId),
          audioId: extractFileId(item.audio, existingMedia?.audioId),
        }
      }

      if (mode === 'update') {
        const mediaId = item.id || existingMedia?.id
        if (mediaId) mediaItem.id = mediaId
      }

      return mediaItem
    })

    // Append từng key FormData array object
    mediaFiles.forEach((item, index) => {
      if (item.id) formData.append(`mediaFiles[${index}].id`, item.id)
      if (item.videoId)
        formData.append(`mediaFiles[${index}].videoId`, item.videoId)
      if (item.imageId)
        formData.append(`mediaFiles[${index}].imageId`, item.imageId)
      if (item.audioId)
        formData.append(`mediaFiles[${index}].audioId`, item.audioId)
    })

    if (mode === 'create') {
      createMutation.mutate(formData)
    } else {
      updateMutation.mutate(formData)
    }
  }

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'THÊM MỚI SỰ KIỆN' : 'CẬP NHẬT SỰ KIỆN'}
      styles={{ body: { overflow: 'auto' } }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={isDrawerDisabled}
            className="min-w-[160px] h-10"
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => form.submit()}
            disabled={isDrawerDisabled}
            className="min-w-[160px] h-10"
          >
            {isSubmitting ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </DrawerFooter>
      }
    >
      <Spin spinning={isFetching} description="Đang tải dữ liệu...">
        <div className="min-h-[300px]">
          {(mode === 'create' || eventDetail) && (
            <EventForm form={form} mode={mode} onSubmit={handleSubmit} />
          )}
        </div>
      </Spin>
    </Drawer>
  )
}
